/**
 * Pure-SVG grouped bar chart for Scorecard Prior / Current / Next.
 *
 * Intentionally avoids Recharts / ResponsiveContainer — those depend on
 * ResizeObserver DOM measurements that return zero-width in Puppeteer's
 * headless PDF render, producing a completely blank chart area.
 * A fixed-viewBox SVG with `width="100%"` renders correctly in all contexts.
 */

import React from "react";
import type { DimensionScorecard } from "../../../utils/forecast/monthlyConsultation";
import { C } from "../../alignment-advantage/shared/constants";

export interface BarGroupProps {
  rows: DimensionScorecard[];
  /**
   * When true, omit the outer bordered white shell (print PDF already wraps
   * the chart in a card). Defaults to false for the web chapter.
   */
  bare?: boolean;
  /**
   * Optional SVG plot height in viewBox units. Defaults to 200.
   * Print Scorecard uses a taller band to match Canva page-06.
   */
  plotHeight?: number;
}

/** Default SVG canvas size; scaled via `width="100%"` in the DOM. */
const DEFAULT_VIEW_W = 560;
const DEFAULT_VIEW_H = 200;

/** Margins inside the SVG canvas for axes and labels. */
const M_LEFT = 28;
const M_RIGHT = 12;
const M_TOP = 10;
const M_BOTTOM = 32;

const Y_TICKS = [0, 25, 50, 75, 100];

interface BarSeries {
  key: "priorPct" | "pct" | "nextPct";
  label: string;
  fill: string;
  opacity: number;
}

const SERIES: BarSeries[] = [
  { key: "priorPct", label: "Prior", fill: C.muted, opacity: 0.55 },
  { key: "pct", label: "Current", fill: C.coral, opacity: 1 },
  { key: "nextPct", label: "Next", fill: C.navy, opacity: 1 },
];

/**
 * Grouped bar chart rendered as a fixed-viewBox SVG.
 * Renders identically in browser and in Puppeteer headless PDF mode.
 */
export const BarGroup: React.FC<BarGroupProps> = ({
  rows,
  bare = false,
  plotHeight,
}) => {
  const viewH =
    typeof plotHeight === "number" && Number.isFinite(plotHeight) && plotHeight > 80
      ? Math.round(plotHeight)
      : DEFAULT_VIEW_H;
  const viewW = DEFAULT_VIEW_W;
  const plotW = viewW - M_LEFT - M_RIGHT;
  const plotH = viewH - M_TOP - M_BOTTOM;

  /** Convert a 0–100 value to an SVG y coordinate (SVG y=0 is top). */
  const valToY = (val: number): number => M_TOP + plotH - (val / 100) * plotH;

  const groupCount = rows.length;
  const groupW = groupCount > 0 ? plotW / groupCount : plotW;

  /** Each group: 3 bars + narrow gaps; centred in the group slot. */
  const barW = Math.floor(groupW * 0.2);
  const barGap = 2;
  const tripleW = barW * 3 + barGap * 2;

  const chart = (
    <svg
      width="100%"
      height={viewH + 18}
      viewBox={`0 0 ${String(viewW)} ${String(viewH + 18)}`}
      aria-label="Scorecard bar chart"
      role="img"
      style={{ display: "block" }}
    >
      {Y_TICKS.map((tick) => {
        const y = valToY(tick);
        return (
          <g key={tick}>
            <line
              x1={M_LEFT}
              y1={y}
              x2={viewW - M_RIGHT}
              y2={y}
              stroke={C.border}
              strokeDasharray="3 3"
              strokeWidth={0.8}
            />
            <text
              x={M_LEFT - 4}
              y={y + 4}
              textAnchor="end"
              fontSize={8}
              fill={C.muted}
            >
              {String(tick)}
            </text>
          </g>
        );
      })}

      {rows.map((row, gi) => {
        const slotLeft = M_LEFT + gi * groupW;
        const barsLeft = slotLeft + (groupW - tripleW) / 2;

        return SERIES.map((s, si) => {
          const raw = row[s.key];
          const val = Math.max(0, Math.min(100, raw));
          const barH = (val / 100) * plotH;
          const bx = barsLeft + si * (barW + barGap);
          const by = valToY(val);

          return (
            <rect
              key={`${row.label}-${s.key}`}
              x={bx}
              y={by}
              width={barW}
              height={barH}
              fill={s.fill}
              opacity={s.opacity}
              rx={2}
            />
          );
        });
      })}

      {rows.map((row, gi) => (
        <text
          key={row.label}
          x={M_LEFT + gi * groupW + groupW / 2}
          y={M_TOP + plotH + 16}
          textAnchor="middle"
          fontSize={9}
          fill={C.muted}
        >
          {row.label}
        </text>
      ))}

      {SERIES.map((s, i) => {
        const lx = M_LEFT + i * 90;
        const ly = viewH + 10;
        return (
          <g key={s.key}>
            <rect
              x={lx}
              y={ly - 7}
              width={10}
              height={10}
              rx={2}
              fill={s.fill}
              opacity={s.opacity}
            />
            <text x={lx + 14} y={ly + 3} fontSize={9} fill={C.muted}>
              {s.label}
            </text>
          </g>
        );
      })}
    </svg>
  );

  if (bare === true) {
    return <div style={{ width: "100%" }}>{chart}</div>;
  }

  return (
    <div
      className="w-full rounded-2xl p-3"
      style={{
        background: "rgba(255,255,255,0.70)",
        border: `1px solid ${C.border}`,
      }}
    >
      {chart}
    </div>
  );
};
